import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import { orderStatus } from '@/theme/status';
import type { EstadoOrden, MotivoCancelacion } from '@/types/domain';
import type { Order } from '@/types/orders';
import { motivoCancelacionMeta, nextEstados } from './orders-format';
import { ORDER_STATUS_FORM_FIELDS, updateOrderStatusPayload } from './orders-payload';
import { ORDER_LIMITS, validateStatusForm } from './orders-validation';
import { useUpdateOrderStatus } from './use-orders';

/**
 * Cambio de estado de una orden (`PATCH .../orders/:orderId/estado`, solo ADMIN).
 *
 * El estado no se elige libre: el backend valida la transición con una máquina de estados y responde
 * 400 si el salto no existe (y 409 si es el mismo estado en el que ya está). La pantalla solo ofrece
 * los destinos REALES (`nextEstados`, espejo de `TRANSICIONES` de `orders.service.ts`), así que el
 * error de transición solo aparece si alguien mueve la orden desde otro lado mientras el diálogo
 * está abierto — y aun así se muestra el mensaje del backend.
 *
 * El `motivo` es opcional en el contrato, pero es lo único que explica el cambio en el historial: al
 * cancelar se pide (ese texto se le manda al admin por WhatsApp) y en el resto de estados queda como
 * nota de por qué se movió.
 *
 * **Motivo de cancelación** (3-oct-2026): al elegir «Cancelada» aparece un `<Select>` OBLIGATORIO con
 * la lista cerrada de por qué se cancela (`motivoCancelacionMeta`). La razón de que sea lista cerrada y
 * no un texto más: esto es un dato para contar —cuántos pedidos se perdieron porque el cliente se
 * arrepiente, cuántos no se pudieron cobrar, cuántos eran pruebas—, y eso solo se puede medir si todos
 * eligen de la misma lista. El `motivo` en texto libre sigue ahí como nota para el admin.
 */

export interface OrderStatusDialogProps {
  open: boolean;
  onClose: () => void;
  /** Orden a mover (`null` = diálogo cerrado). */
  order: Order | null;
}

export function OrderStatusDialog({ open, onClose, order }: OrderStatusDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const updateStatus = useUpdateOrderStatus(tenantId);

  const [estado, setEstado] = useState<EstadoOrden | ''>('');
  const [motivo, setMotivo] = useState('');
  const [motivoCancelacion, setMotivoCancelacion] = useState<MotivoCancelacion | ''>('');
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const destinos = order ? nextEstados(order.estado) : [];

  /** Cada apertura arranca limpia: si solo hay un destino posible, ya viene elegido. */
  useEffect(() => {
    if (!open) return;

    const posibles = order ? nextEstados(order.estado) : [];
    setEstado(posibles.length === 1 ? posibles[0] : '');
    setMotivo('');
    setMotivoCancelacion('');
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, order]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!order) return;

    const nextErrors = validateStatusForm(estado, motivo, motivoCancelacion);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await updateStatus.mutateAsync({
        orderId: order.id,
        payload: updateOrderStatusPayload(estado as EstadoOrden, motivo, motivoCancelacion),
      });
      onClose();
    } catch (error) {
      setErrors(formErrorsFromApiError(error, ORDER_STATUS_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  if (!order) return null;

  const actual = orderStatus[order.estado];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cambiar el estado de la orden"
      description={`${order.clienteNombre} · hoy está en «${actual.label}»`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateStatus.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="order-status-form" loading={updateStatus.isPending}>
            Guardar el cambio
          </Button>
        </>
      }
    >
      <form id="order-status-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        <div className="flex items-center gap-2">
          <span className="text-caption text-ink-muted">Estado actual:</span>
          <StatusBadge label={actual.label} tone={actual.tone} />
        </div>

        <Field
          label="Pasar a"
          htmlFor="orderEstado"
          required
          hint={`Desde «${actual.label}» el pedido solo puede ir a: ${destinos.map((item) => orderStatus[item].label).join(', ') || 'ningún estado (es final)'}.`}
          error={fieldError(errors, 'estado')}
        >
          <Select
            id="orderEstado"
            name="estado"
            value={estado}
            invalid={Boolean(fieldError(errors, 'estado'))}
            onChange={(event) => setEstado(event.target.value as EstadoOrden | '')}
          >
            <option value="">Elige el estado…</option>
            {destinos.map((item) => (
              <option key={item} value={item}>
                {orderStatus[item].label}
              </option>
            ))}
          </Select>
        </Field>

        {/* El motivo de cancelación SOLO aparece al cancelar: en los demás estados no aplica y mandarlo
            sería un 400 del backend. */}
        {estado === 'CANCELADA' ? (
          <Field
            label="Por qué se cancela"
            htmlFor="orderMotivoCancelacion"
            required
            hint="Queda guardado en la orden. Sirve para saber cuántos pedidos se pierden por cada causa."
            error={fieldError(errors, 'motivoCancelacion')}
          >
            <Select
              id="orderMotivoCancelacion"
              name="motivoCancelacion"
              value={motivoCancelacion}
              invalid={Boolean(fieldError(errors, 'motivoCancelacion'))}
              onChange={(event) =>
                setMotivoCancelacion(event.target.value as MotivoCancelacion | '')
              }
            >
              <option value="">Elegí el motivo…</option>
              {(Object.keys(motivoCancelacionMeta) as MotivoCancelacion[]).map((clave) => (
                <option key={clave} value={clave}>
                  {motivoCancelacionMeta[clave]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field
          label="Motivo"
          htmlFor="orderMotivo"
          hint="Queda en el historial de la orden. Al cancelar, el admin lo recibe por WhatsApp."
          error={fieldError(errors, 'motivo')}
        >
          <Textarea
            id="orderMotivo"
            name="motivo"
            rows={3}
            maxLength={ORDER_LIMITS.motivo}
            placeholder={
              estado === 'CANCELADA'
                ? 'Ej. el cliente no contestó'
                : 'Ej. sale con la transportadora del martes'
            }
            value={motivo}
            invalid={Boolean(fieldError(errors, 'motivo'))}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </Field>

        {estado === 'CANCELADA' ? (
          <Alert tone="warning">
            Cancelar es definitivo: «Cancelada» es un estado final, así que la orden no se puede
            volver a mover ni se le puede cargar la guía. Si el pedido solo está en pausa, déjalo en
            el estado en el que está.
          </Alert>
        ) : null}
      </form>
    </Modal>
  );
}

